import React, { useEffect, useState } from "react";
import { TrendUp } from "@phosphor-icons/react";
import { api } from "../lib/api";

/**
 * Animated horizontal marquee strip showing recent target-hit wins.
 * Pulls /api/public/recent-wins (public, no auth) and loops through items.
 * Auto-hides if fewer than 3 wins exist (avoids awkward small sets).
 */
export default function WinsTickerStrip() {
  const [wins, setWins] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get("/public/recent-wins?limit=15")
      .then(({ data }) => {
        if (!cancelled) setWins(data?.items || []);
      })
      .catch(() => { /* silent — strip just hides */ });
    return () => { cancelled = true; };
  }, []);

  if (!wins || wins.length < 3) return null;

  // Duplicate the list so the CSS marquee loops seamlessly
  const loop = [...wins, ...wins];

  return (
    <div
      className="relative w-full overflow-hidden border-y border-pos/30 bg-pos-soft/15 backdrop-blur-sm"
      data-testid="wins-ticker-strip"
    >
      {/* Edge fade masks */}
      <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-app to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-app to-transparent z-10 pointer-events-none" />

      <div className="flex items-stretch">
        {/* Static "RECENT WINS" label */}
        <div className="shrink-0 flex items-center gap-1.5 px-3 py-2 border-r border-pos/30 bg-pos-soft/40 text-[10px] font-mono uppercase tracking-widest text-pos">
          <TrendUp size={11} weight="bold" />
          Recent wins
        </div>

        {/* Animated marquee track */}
        <div className="flex-1 overflow-hidden">
          <div
            className="flex items-center gap-8 py-2 animate-marquee whitespace-nowrap will-change-transform"
            style={{ animationDuration: `${Math.max(30, loop.length * 4)}s` }}
          >
            {loop.map((w, i) => (
              <span
                key={`${w.symbol}-${i}`}
                className="inline-flex items-center gap-2 text-[11px] font-mono"
              >
                <span className="text-muted-fg uppercase tracking-widest">
                  {w.closed_relative || "recently"}
                </span>
                <span className="text-primary-fg">{w.symbol}</span>
                <span className="text-pos tabular-nums">+{w.pnl_pct}%</span>
                {w.days_held ? (
                  <span className="text-muted-fg">
                    · {w.days_held}d held
                  </span>
                ) : null}
                <span className="text-pos">→</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
